import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { Shortcut } from 'app/layout/common/shortcuts/shortcuts.types';
import type { Observable } from 'rxjs';
import { map, ReplaySubject, switchMap, take, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ShortcutsService {
  private readonly shortcuts: ReplaySubject<Shortcut[]> = new ReplaySubject<Shortcut[]>(1);

  private httpClient = inject(HttpClient);

  public readonly shortcuts$: Observable<Shortcut[]> = this.shortcuts.asObservable();

  /**
   * Get all messages
   */
  public getAll(): Observable<Shortcut[]> {
    return this.httpClient.get<Shortcut[]>('api/common/shortcuts').pipe(
      tap((shortcuts) => {
        this.shortcuts.next(shortcuts);
      })
    );
  }

  /**
   * Create a shortcut
   *
   * @param shortcut
   */
  public create(shortcut: Shortcut): Observable<Shortcut> {
    return this.shortcuts$.pipe(
      take(1),
      switchMap((shortcuts) =>
        this.httpClient.post<Shortcut>('api/common/shortcuts', { shortcut }).pipe(
          map((newShortcut) => {
            // Update the shortcuts with the new shortcut
            this.shortcuts.next([...shortcuts, newShortcut]);

            // Return the new shortcut from observable
            return newShortcut;
          })
        )
      )
    );
  }

  /**
   * Update the shortcut
   *
   * @param id
   * @param shortcut
   */
  public update(id: string, shortcut: Shortcut): Observable<Shortcut> {
    return this.shortcuts$.pipe(
      take(1),
      switchMap((shortcuts) =>
        this.httpClient
          .patch<Shortcut>('api/common/shortcuts', {
            id,
            shortcut,
          })
          .pipe(
            map((updatedShortcut: Shortcut) => {
              // Find the index of the updated shortcut
              const index = shortcuts.findIndex((item) => item.id === id);

              // Update the shortcut
              shortcuts[index] = updatedShortcut;

              // Update the shortcuts
              this.shortcuts.next(shortcuts);

              // Return the updated shortcut
              return updatedShortcut;
            })
          )
      )
    );
  }

  /**
   * Delete the shortcut
   *
   * @param id
   */
  public delete(id: string): Observable<boolean> {
    return this.shortcuts$.pipe(
      take(1),
      switchMap((shortcuts) =>
        this.httpClient.delete<boolean>('api/common/shortcuts', { params: { id } }).pipe(
          map((isDeleted: boolean) => {
            // Find the index of the deleted shortcut
            const index = shortcuts.findIndex((item) => item.id === id);

            // Delete the shortcut
            shortcuts.splice(index, 1);

            // Update the shortcuts
            this.shortcuts.next(shortcuts);

            // Return the deleted status
            return isDeleted;
          })
        )
      )
    );
  }
}
